/**
 * SuperBrain — Public Status Page
 *
 * No auth, no admin info — just a green/yellow/red dot and a friendly
 * "operational" / "degraded" message that paying customers can bookmark.
 * Polls /api/status every 60s. Sanitised — never exposes internal failure
 * detail.
 *
 * © 2026 A-1 Affiliate Marketing LLC
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './AuthPage.css';

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const POLL_MS = 60_000;

const COLOR_MAP = {
  operational: { dot: '#2bd17b', label: 'OPERATIONAL', border: '#1f8a4c', bg: 'rgba(31,138,76,0.12)' },
  degraded: { dot: '#ff5252', label: 'DEGRADED', border: '#c93838', bg: 'rgba(201,56,56,0.12)' },
  unknown: { dot: '#a8a8a8', label: 'UNKNOWN', border: '#6b6b6b', bg: 'rgba(107,107,107,0.12)' },
};

const fmtAgo = (iso) => {
  if (!iso) return 'never';
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.round(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} minutes ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  return `${Math.round(h / 24)} day${h >= 48 ? 's' : ''} ago`;
};

const StatusPage = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  const load = async () => {
    try {
      const r = await fetch(`${API}/status`);
      const j = await r.json();
      setData(j);
      setErr('');
    } catch (e) {
      setErr('Could not reach status service.');
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, []);

  const color = COLOR_MAP[data?.status] || COLOR_MAP.unknown;

  return (
    <div className="auth-page" data-testid="status-page">
      <div className="auth-container" style={{ maxWidth: 520 }}>
        <div className="auth-header">
          <h1>SuperBrain</h1>
          <p className="auth-subtitle">System Status</p>
        </div>

        <div
          className="auth-form"
          data-testid={`status-pill-${data?.status || 'loading'}`}
          style={{
            background: color.bg,
            border: `1px solid ${color.border}`,
            borderRadius: '6px',
            padding: '1.5rem 1.25rem',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '0.75rem',
            }}
          >
            <span
              data-testid="status-dot"
              style={{
                width: '0.9rem',
                height: '0.9rem',
                borderRadius: '999px',
                background: color.dot,
                boxShadow: data?.status === 'degraded' ? `0 0 10px ${color.dot}` : 'none',
                animation: data?.status === 'degraded' ? 'sb-pulse 1.6s ease-in-out infinite' : 'none',
              }}
            />
            <strong style={{ letterSpacing: '0.16em', fontSize: '0.95rem' }}>
              {color.label}
            </strong>
          </div>

          <p style={{ margin: 0, fontSize: '1rem', color: '#c8c0aa', lineHeight: 1.6 }} data-testid="status-message">
            {err || data?.message || 'Checking…'}
          </p>

          <p style={{ marginTop: '1rem', fontSize: '0.78rem', color: '#8a8270' }}>
            Last checked: {fmtAgo(data?.checked_at)}
          </p>
        </div>

        <p
          className="auth-help"
          style={{ marginTop: '1.25rem', textAlign: 'center' }}
        >
          Monitoring{' '}
          <a
            href={data?.monitored_url || 'https://superbrain.life'}
            target="_blank"
            rel="noreferrer"
            style={{ color: '#c9a84c' }}
          >
            superbrain.life
          </a>
          . Updates automatically every 60 seconds.
        </p>

        <Link
          to="/"
          className="auth-submit"
          style={{
            display: 'block',
            textAlign: 'center',
            textDecoration: 'none',
            marginTop: '1rem',
          }}
          data-testid="status-home-link"
        >
          Back to SuperBrain
        </Link>

        <div className="auth-footer">
          <p>✝ GOD BLESS EVERYONE ✝</p>
          <p className="auth-copyright">© 2026 A-1 Affiliate Marketing LLC</p>
        </div>

        <style>{`@keyframes sb-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(1.15); }
        }`}</style>
      </div>
    </div>
  );
};

export default StatusPage;
